import React, { useState, useEffect, useCallback } from 'react';
import { getProductPriceHistory, getChemicalPriceHistory } from '../../api/priceCalculation';
import styles from './PriceCalculation.module.scss';
import { Search, RefreshCw, X } from 'lucide-react';

const PERIOD_OPTIONS = [
  { value: '10', label: 'Last 10' },
  { value: '20', label: 'Last 20' },
  { value: '30', label: 'Last 30' },
  { value: '50', label: 'Last 50' },
  { value: 'all', label: 'All (max 200)' },
  { value: 'custom', label: 'Custom range' },
];

const HISTORY_TYPES = {
  all: 'all',
  product: 'product',
  chemical: 'chemical',
};

function formatDate(isoStr) {
  if (!isoStr) return '—';
  try {
    const d = new Date(isoStr);
    return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return isoStr;
  }
}

function formatUpdater(row) {
  if (row.created_by_name) return row.created_by_name;
  if (row.created_by_email) return row.created_by_email;
  if (row.created_by != null) return `User #${row.created_by}`;
  return '—';
}

function normalizeRows(rows, type) {
  return (Array.isArray(rows) ? rows : []).map((row) => ({
    ...row,
    historyType: type,
    itemName: type === 'chemical' ? row.chemical_name : row.product_name,
  }));
}

function sortByNewest(rows) {
  return [...rows].sort((a, b) => {
    const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
    const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
    if (tb !== ta) return tb - ta;
    return (b.id || 0) - (a.id || 0);
  });
}

export default function ProductPriceHistory({ onError }) {
  const [historyType, setHistoryType] = useState(HISTORY_TYPES.all);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [period, setPeriod] = useState('50');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    if (onError) onError(null);
    try {
      const limit = period === 'all' || period === 'custom' ? 200 : parseInt(period, 10);
      const baseFilters = { limit };
      if (period === 'custom') {
        if (dateFrom) baseFilters.date_from = new Date(dateFrom).toISOString();
        if (dateTo) baseFilters.date_to = new Date(dateTo + 'T23:59:59.999Z').toISOString();
      }

      const search = searchQuery.trim() || undefined;
      const shouldLoadChemical = historyType === HISTORY_TYPES.all || historyType === HISTORY_TYPES.chemical;
      const shouldLoadProduct = historyType === HISTORY_TYPES.all || historyType === HISTORY_TYPES.product;

      const [chemicalData, productData] = await Promise.all([
        shouldLoadChemical
          ? getChemicalPriceHistory({ ...baseFilters, chemical_name: search })
          : Promise.resolve([]),
        shouldLoadProduct
          ? getProductPriceHistory({ ...baseFilters, product_name: search })
          : Promise.resolve([]),
      ]);

      const merged = sortByNewest([
        ...normalizeRows(chemicalData, 'chemical'),
        ...normalizeRows(productData, 'product'),
      ]).slice(0, limit);

      setList(merged);
    } catch (e) {
      if (onError) onError(e.message);
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [historyType, searchQuery, period, dateFrom, dateTo, onError]);

  useEffect(() => {
    load();
  }, [load]);

  const filterRowStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 16,
    alignItems: 'center',
    marginBottom: 20,
    padding: '12px 16px',
    background: 'var(--card-bg)',
    border: '1px solid var(--border-color)',
    borderRadius: 8,
  };

  const emptyMessage = searchQuery.trim()
    ? `No price history found matching "${searchQuery}". Try a different search or period.`
    : 'No price history yet. Update a chemical or product price, then refresh this tab.';

  return (
    <div className={styles.calcSection}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <button
          type="button"
          className={`${styles.btn} ${historyType === HISTORY_TYPES.all ? styles.btnPrimary : styles.btnSecondary}`}
          onClick={() => setHistoryType(HISTORY_TYPES.all)}
        >
          All history
        </button>
        <button
          type="button"
          className={`${styles.btn} ${historyType === HISTORY_TYPES.chemical ? styles.btnPrimary : styles.btnSecondary}`}
          onClick={() => setHistoryType(HISTORY_TYPES.chemical)}
        >
          Chemical prices
        </button>
        <button
          type="button"
          className={`${styles.btn} ${historyType === HISTORY_TYPES.product ? styles.btnPrimary : styles.btnSecondary}`}
          onClick={() => setHistoryType(HISTORY_TYPES.product)}
        >
          Product prices
        </button>
      </div>

      <p style={{ marginBottom: 16, color: 'var(--secondary-text)', fontSize: 14 }}>
        Chemical and product price changes appear here: previous price, new price, who updated, and when.
        Use <strong>Chemical prices</strong> if you only want chemical updates.
      </p>

      <div className={styles.searchBar} style={{ marginBottom: 20 }}>
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <Search size={18} style={{ position: 'absolute', left: 12, color: 'var(--secondary-text)' }} />
          <input
            type="text"
            placeholder="Search by chemical or product name…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ paddingLeft: 40 }}
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              style={{
                position: 'absolute',
                right: 8,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 4,
                display: 'flex',
                alignItems: 'center',
                color: 'var(--secondary-text)',
              }}
              title="Clear search"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      <div style={filterRowStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ color: 'var(--secondary-text)', fontSize: 14, whiteSpace: 'nowrap' }}>Period:</label>
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            style={{
              minWidth: 140,
              padding: '8px 12px',
              border: '1px solid var(--border-color)',
              borderRadius: 6,
              background: 'var(--card-bg)',
              color: 'var(--primary-text)',
              fontSize: 14,
            }}
          >
            {PERIOD_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        {period === 'custom' && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label style={{ color: 'var(--secondary-text)', fontSize: 14 }}>From:</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                style={{ padding: '6px 8px', background: 'var(--card-bg)', color: 'var(--primary-text)', border: '1px solid var(--border-color)', borderRadius: 6 }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label style={{ color: 'var(--secondary-text)', fontSize: 14 }}>To:</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                style={{ padding: '6px 8px', background: 'var(--card-bg)', color: 'var(--primary-text)', border: '1px solid var(--border-color)', borderRadius: 6 }}
              />
            </div>
          </>
        )}
        <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={load} disabled={loading}>
          <RefreshCw size={14} /> {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {loading && list.length === 0 ? (
        <div className={styles.loading}>Loading price history…</div>
      ) : (
        <div className={styles.tableWrap}>
          <table className={`${styles.table} ${styles.breakdownTable}`}>
            <thead>
              <tr>
                <th>Type</th>
                <th>Name</th>
                <th>New price / kg</th>
                <th>Previous price / kg</th>
                <th>Change</th>
                <th>Updated by</th>
                <th>Date</th>
                <th>Source</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {list.length === 0 ? (
                <tr>
                  <td colSpan={9} className={styles.empty}>
                    {emptyMessage}
                  </td>
                </tr>
              ) : list.map((row) => {
                const prev = row.previous_price_per_kg;
                const curr = row.price_per_kg;
                const hasPrev = prev != null && !Number.isNaN(Number(prev));
                const changeAmount = hasPrev ? curr - prev : null;
                const changePct = hasPrev && prev !== 0 ? ((curr - prev) / prev) * 100 : null;
                const isChemical = row.historyType === 'chemical';
                return (
                  <tr key={`${row.historyType}-${row.id}`}>
                    <td>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: 999,
                          fontSize: 12,
                          fontWeight: 600,
                          background: isChemical ? '#dbeafe' : '#dcfce7',
                          color: isChemical ? '#1e40af' : '#166534',
                        }}
                      >
                        {isChemical ? 'Chemical' : 'Product'}
                      </span>
                    </td>
                    <td style={{ fontWeight: 500, color: 'var(--primary-text)' }}>{row.itemName ?? '—'}</td>
                    <td style={{ color: 'var(--primary-text)' }}>{Number(row.price_per_kg).toFixed(4)}</td>
                    <td style={{ color: 'var(--secondary-text)' }}>
                      {prev != null ? Number(prev).toFixed(4) : <em title="First price set">First price</em>}
                    </td>
                    <td>
                      {changeAmount != null ? (
                        <span
                          style={{
                            color: changeAmount > 0 ? 'var(--accent-color, #2563eb)' : changeAmount < 0 ? '#dc2626' : 'var(--secondary-text)',
                            fontWeight: 500,
                          }}
                        >
                          {changeAmount > 0 ? '↑' : changeAmount < 0 ? '↓' : ''} {changeAmount.toFixed(4)}
                          {changePct != null && ` (${changePct >= 0 ? '+' : ''}${changePct.toFixed(1)}%)`}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--secondary-text)' }}>—</span>
                      )}
                    </td>
                    <td style={{ color: 'var(--primary-text)' }} title={row.created_by_email || undefined}>
                      {formatUpdater(row)}
                    </td>
                    <td style={{ color: 'var(--secondary-text)', fontSize: 13 }}>{formatDate(row.created_at)}</td>
                    <td style={{ color: 'var(--primary-text)' }}>
                      {isChemical ? 'Manual' : (row.is_calculated ? 'Calculated' : 'Manual')}
                    </td>
                    <td style={{ color: 'var(--secondary-text)', fontSize: 13 }}>{row.change_reason ?? '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
